
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';

import {
  Component,
  Inject,
  OnInit,
  PLATFORM_ID
} from '@angular/core';

import { NgForm } from '@angular/forms';
import { ReCaptchaV3Service } from 'ng-recaptcha';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-contact-us',
  templateUrl: './contact-us.component.html',
  styleUrls: ['./contact-us.component.scss']
})
export class ContactUsComponent implements OnInit {

  // Alderhold Logicfu API
  private readonly apiUrl =
    'https://alderhold.logicfu.in/api/public/alderhold/views/contact-us/submit';

  // Fixed Company ID
  readonly companyId =
    '13820010-9538-45c8-97c8-8f04eea85f3a';

  // Loading state
  isLoading = false;

  // Optional products
  selectedProductIds: string[] = [];

  // Existing form fields
  // The Subject input is used as Phone Number.
  formData = {
    name: '',
    email: '',
    subject: '',
    message: '',
    company_name: ''
  };

  constructor(
    private http: HttpClient,
    private recaptchaV3Service: ReCaptchaV3Service,
    @Inject(PLATFORM_ID) private platformId: Object
  ) { }

  ngOnInit(): void {

    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }

  }

  // Submit Contact Form
  async onSubmit(form: NgForm): Promise<void> {

    // Prevent duplicate submissions
    if (this.isLoading || !isPlatformBrowser(this.platformId)) {
      return;
    }

    // Trim form values
    const name = this.formData.name.trim();
    const email = this.formData.email.trim();
    const phone = this.formData.subject.trim();
    const message = this.formData.message.trim();

    // Validation patterns
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phonePattern = /^[0-9]{10}$/;

    // STEP 1: Form validation
    if (
      form.invalid ||
      !name ||
      !emailPattern.test(email) ||
      !phonePattern.test(phone) ||
      !message
    ) {

      form.control.markAllAsTouched();

      await Swal.fire({
        title: 'Form Submission Failed!',
        text: 'Please enter a valid name, email, 10-digit phone number and message.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });

      return;
    }

    // Start loading
    this.isLoading = true;

    try {

      // STEP 2: Generate Google reCAPTCHA v3 token
      // Compatible with RxJS 6
      const recaptchaToken = await this.recaptchaV3Service
        .execute('contact_form_submit')
        .toPromise();

      if (!recaptchaToken) {
        throw new Error('Unable to generate reCAPTCHA v3 token.');
      }

      // STEP 3: Prepare API record
      const value: any = {

        // Fixed company
        company: this.companyId,

        // Form values
        name: name,
        email: email,
        phone: phone,

        // Rich-text message
        message: {
          version: 1,
          ops: [
            {
              insert: message + '\n'
            }
          ]
        }

      };

      // Optional company name
      if (this.formData.company_name.trim()) {
        value.company_name =
          this.formData.company_name.trim();
      }

      // Optional products
      if (this.selectedProductIds.length > 0) {
        value.products = this.selectedProductIds;
      }

      // STEP 4: Match Logicfu API payload
      const payload = {
        value: value
      };

      // STEP 5: Send reCAPTCHA v3 token in headers
      const headers = new HttpHeaders({
        'Content-Type': 'application/json',
        'X-Captcha-Token': recaptchaToken
      });

      // STEP 6: Submit to Alderhold Logicfu
      const response = await this.http.post(
        this.apiUrl,
        payload,
        {
          headers: headers,
          observe: 'response',
          responseType: 'text'
        }
      ).toPromise();

      console.log(
        'Contact Form Submitted Successfully:',
        response?.status
      );

      // STEP 7: Reset form
      form.resetForm();

      this.formData = {
        name: '',
        email: '',
        subject: '',
        message: '',
        company_name: ''
      };

      this.selectedProductIds = [];

      // STEP 8: Success message
      await Swal.fire({
        title: 'Form Submitted Successfully!',
        text: 'Thank you for contacting us.',
        icon: 'success',
        confirmButtonText: 'OK'
      });

    } catch (error) {

      const apiError: any = error;

      console.error('Contact Submission Error:', {
        status: apiError?.status,
        statusText: apiError?.statusText,
        message: apiError?.message,
        response: apiError?.error
      });

      // Parse API error response
      let errorBody = apiError?.error;

      if (typeof errorBody === 'string') {

        try {
          errorBody = JSON.parse(errorBody);
        } catch {
          errorBody = null;
        }

      }

      const serverMessage = errorBody?.message;

      const errorMessage = Array.isArray(serverMessage)
        ? serverMessage.join(' ')
        : typeof serverMessage === 'string'
          ? serverMessage
          : apiError?.message ||
          'Unable to verify or submit the form. Please try again.';

      // Error message
      await Swal.fire({
        title: 'Form Not Submitted!',
        text: errorMessage,
        icon: 'error',
        confirmButtonText: 'OK'
      });

    } finally {

      // Stop loading
      this.isLoading = false;

    }

  }

}
